import { FC, ReactNode } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { avatarColorClass } from './avatarColor';

export type AvatarSize =
  '3xs' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type AvatarVariant = 'default' | 'shadow' | 'outline';
export type AvatarShape = 'circle' | 'square';

export interface AvatarProps {
  /** Size of the avatar */
  size?: AvatarSize;

  /** Shape of the avatar */
  shape?: AvatarShape;

  /** Image source URL */
  src?: string;

  /**
   * Skip Next's image optimiser, for hosts that aren't on its allow-list
   * (e.g. signed CDN URLs).
   */
  unoptimized?: boolean;

  /** Called when the image fails to load, e.g. an expired link. */
  onImageError?: () => void;

  /** Custom icon/SVG to display instead of image */
  icon?: ReactNode;

  /** Initials to display when no image or icon is provided */
  initials?: string;

  /**
   * Colours the initials by this key (usually the user's id), the same way
   * the Swift apps do, so a person keeps one colour everywhere.
   */
  colorKey?: string;

  /** Visual variant of the avatar */
  variant?: AvatarVariant;

  /** Custom outline color class (only applies to 'outline' variant, defaults to 'outline-mf-blue') */
  outlineColorClass?: string;

  /** Whether to show padding/gap between the ring and avatar content (only applies to 'ring' variant, defaults to true) */
  withPadding?: boolean;

  /** Use gold gradient ring for premium/pro users (overrides outlineColorClass when true) */
  gradientRing?: boolean;

  /** Custom badge element (SVG/icon) to display at bottom-right corner */
  badge?: ReactNode;

  /** Shows a green online indicator dot at bottom-right */
  isOnline?: boolean;

  /** Shows a verified badge at bottom-right */
  isVerified?: boolean;

  /** Alt text for the image */
  alt?: string;

  /** Primary label text displayed to the right of the avatar (e.g., name) */
  primaryLabel?: string;

  /** Secondary label text displayed below primary label (e.g., email) */
  secondaryLabel?: string;

  /** Additional CSS classes */
  className?: string;
}

// Size configurations for avatar dimensions
const sizeConfig: Record<
  AvatarSize,
  {
    container: string;
    imageSize: string;
    text: string;
    onlineBadge: string;
    verifiedBadge: string;
    badgePosition: string;
    outlineWidth: string;
    outlineOffset: string;
    // Gradient ring positioning (negative inset to render outside the avatar)
    gradientRingInset: string; // Full ring position (border + gap)
    gradientGapInset: string; // White gap layer position (between gradient and avatar)
    squareRadius: string;
    labelPrimary: string;
    labelSecondary: string;
    labelGap: string;
  }
> = {
  '3xs': {
    container: 'w-4 h-4',
    imageSize: 'w-4 h-4',
    text: 'text-[7px]',
    onlineBadge: 'w-1.5 h-1.5 border',
    verifiedBadge: 'w-2 h-2',
    badgePosition: 'bottom-[-2px] right-[-2px]',
    outlineWidth: 'outline-[1.5px]',
    outlineOffset: 'outline-offset-[1px]',
    gradientRingInset: 'inset-[-2.5px]',
    gradientGapInset: 'inset-[-1px]',
    squareRadius: 'rounded-sm',
    labelPrimary: 'text-xs',
    labelSecondary: 'text-[10px]',
    labelGap: 'gap-1',
  },
  xxs: {
    container: 'w-5 h-5',
    imageSize: 'w-5 h-5',
    text: 'text-[8px]',
    onlineBadge: 'w-1.5 h-1.5 border',
    verifiedBadge: 'w-2.5 h-2.5',
    badgePosition: 'bottom-[-2px] right-[-2px]',
    outlineWidth: 'outline-2',
    outlineOffset: 'outline-offset-[1px]',
    gradientRingInset: 'inset-[-3px]',
    gradientGapInset: 'inset-[-1px]',
    squareRadius: 'rounded-sm',
    labelPrimary: 'text-xs',
    labelSecondary: 'text-[10px]',
    labelGap: 'gap-1',
  },
  xs: {
    container: 'w-8 h-8',
    imageSize: 'w-8 h-8',
    text: 'text-xs',
    onlineBadge: 'w-2.5 h-2.5 border',
    verifiedBadge: 'w-3.5 h-3.5',
    badgePosition: 'bottom-[-2px] right-[-2px]',
    outlineWidth: 'outline-[2.5px]',
    outlineOffset: 'outline-offset-[1.5px]',
    gradientRingInset: 'inset-[-4px]',
    gradientGapInset: 'inset-[-1.5px]',
    squareRadius: 'rounded-md',
    labelPrimary: 'text-xs',
    labelSecondary: 'text-xs',
    labelGap: 'gap-1.5',
  },
  sm: {
    container: 'w-10 h-10',
    imageSize: 'w-10 h-10',
    text: 'text-sm',
    onlineBadge: 'w-3 h-3 border-2',
    verifiedBadge: 'w-4 h-4',
    badgePosition: 'bottom-[-2px] right-[-2px]',
    outlineWidth: 'outline-[3px]',
    outlineOffset: 'outline-offset-2',
    gradientRingInset: 'inset-[-5px]',
    gradientGapInset: 'inset-[-2px]',
    squareRadius: 'rounded-lg',
    labelPrimary: 'text-base',
    labelSecondary: 'text-sm',
    labelGap: 'gap-1.5',
  },
  md: {
    container: 'w-12 h-12',
    imageSize: 'w-12 h-12',
    text: 'text-base',
    onlineBadge: 'w-3.5 h-3.5 border-2',
    verifiedBadge: 'w-5 h-5',
    badgePosition: 'bottom-[-3px] right-[-3px]',
    outlineWidth: 'outline-[3px]',
    outlineOffset: 'outline-offset-2',
    gradientRingInset: 'inset-[-5px]',
    gradientGapInset: 'inset-[-2px]',
    squareRadius: 'rounded-xl',
    labelPrimary: 'text-sm',
    labelSecondary: 'text-sm',
    labelGap: 'gap-2',
  },
  lg: {
    container: 'w-14 h-14',
    imageSize: 'w-14 h-14',
    text: 'text-lg',
    onlineBadge: 'w-4 h-4 border-2',
    verifiedBadge: 'w-6 h-6',
    badgePosition: 'bottom-[-3px] right-[-3px]',
    outlineWidth: 'outline-[3px]',
    outlineOffset: 'outline-offset-2',
    gradientRingInset: 'inset-[-5px]',
    gradientGapInset: 'inset-[-2px]',
    squareRadius: 'rounded-xl',
    labelPrimary: 'text-base',
    labelSecondary: 'text-sm',
    labelGap: 'gap-2',
  },
  xl: {
    container: 'w-16 h-16',
    imageSize: 'w-16 h-16',
    text: 'text-xl',
    onlineBadge: 'w-4.5 h-4.5 border-2',
    verifiedBadge: 'w-7 h-7',
    badgePosition: 'bottom-[-4px] right-[-4px]',
    outlineWidth: 'outline-[3.5px]',
    outlineOffset: 'outline-offset-[2.5px]',
    gradientRingInset: 'inset-[-6px]',
    gradientGapInset: 'inset-[-2.5px]',
    squareRadius: 'rounded-2xl',
    labelPrimary: 'text-base',
    labelSecondary: 'text-sm',
    labelGap: 'gap-2.5',
  },
  '2xl': {
    container: 'w-20 h-20',
    imageSize: 'w-20 h-20',
    text: 'text-2xl',
    onlineBadge: 'w-5 h-5 border-2',
    verifiedBadge: 'w-8 h-8',
    badgePosition: 'bottom-[-4px] right-[-4px]',
    outlineWidth: 'outline-4',
    outlineOffset: 'outline-offset-[3px]',
    gradientRingInset: 'inset-[-7px]',
    gradientGapInset: 'inset-[-3px]',
    squareRadius: 'rounded-2xl',
    labelPrimary: 'text-lg',
    labelSecondary: 'text-base',
    labelGap: 'gap-3',
  },
};

// Default user icon SVG
const DefaultUserIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 4a4 4 0 100 8 4 4 0 000-8z" />
    <path d="M12 14c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5z" />
  </svg>
);

// Verified badge SVG
const VerifiedBadge: FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path
      d="M12 2L14.09 4.26L17 3.34L17.34 6.34L20.26 7.09L19.18 10L21 12L19.18 14L20.26 16.91L17.34 17.66L17 20.66L14.09 19.74L12 22L9.91 19.74L7 20.66L6.66 17.66L3.74 16.91L4.82 14L3 12L4.82 10L3.74 7.09L6.66 6.34L7 3.34L9.91 4.26L12 2Z"
      fill="#00B573"
    />
    <path
      d="M10 14.4L7.6 12L6.5 13.1L10 16.6L17.5 9.1L16.4 8L10 14.4Z"
      fill="white"
    />
  </svg>
);

const Avatar: FC<AvatarProps> = ({
  size = 'md',
  shape = 'circle',
  src,
  unoptimized = false,
  onImageError,
  icon,
  initials,
  colorKey,
  variant = 'default',
  outlineColorClass = 'outline-mf-blue',
  withPadding = true,
  gradientRing = false,
  badge,
  isOnline = false,
  isVerified = false,
  alt = 'Avatar',
  primaryLabel,
  secondaryLabel,
  className,
}) => {
  const config = sizeConfig[size];
  const isCircle = shape === 'circle';
  const shapeClass = isCircle ? 'rounded-full' : config.squareRadius;
  const hasLabel = primaryLabel || secondaryLabel;
  const useGradient = variant === 'outline' && gradientRing;

  // Determine what badge to show (priority: badge > isVerified > isOnline)
  const renderBadge = () => {
    if (badge) {
      return (
        <span
          className={classNames(
            'absolute flex items-center justify-center',
            config.badgePosition,
          )}
        >
          {badge}
        </span>
      );
    }

    if (isVerified) {
      return (
        <span
          className={classNames(
            'absolute flex items-center justify-center',
            config.badgePosition,
          )}
        >
          <VerifiedBadge className={config.verifiedBadge} />
        </span>
      );
    }

    if (isOnline) {
      return (
        <span
          className={classNames(
            'absolute rounded-full bg-mf-green border-white',
            config.onlineBadge,
            config.badgePosition,
          )}
        />
      );
    }

    return null;
  };

  // Render avatar content (priority: src > icon > initials > fallback)
  const renderContent = () => {
    if (src) {
      return (
        <Image
          src={src}
          alt={alt}
          fill
          sizes="100%"
          unoptimized={unoptimized}
          onError={onImageError}
          className={classNames('object-cover', shapeClass)}
        />
      );
    }

    if (icon) {
      return (
        <span className="flex h-full w-full items-center justify-center text-mf-blue">
          {icon}
        </span>
      );
    }

    if (initials) {
      return (
        // Opaque base under the translucent wash, so overlapping avatars in
        // a group don't show through each other.
        <span
          className={classNames(
            'flex h-full w-full items-center justify-center',
            colorKey && 'bg-surface-panel',
            shapeClass,
          )}
        >
          <span
            className={classNames(
              'flex h-full w-full items-center justify-center font-semibold',
              colorKey ? avatarColorClass(colorKey) : 'text-mf-off-black-600',
              config.text,
            )}
          >
            {initials.slice(0, 2).toUpperCase()}
          </span>
        </span>
      );
    }

    // Default fallback - user icon
    return (
      <span className="flex h-full w-full items-center justify-center">
        <DefaultUserIcon className="h-3/5 w-3/5 text-mf-blue" />
      </span>
    );
  };

  // Container styles
  const containerStyles = classNames(
    'relative inline-flex shrink-0',
    config.container,
    className,
  );

  // Avatar styles (for non-gradient ring)
  const avatarStyles = classNames(
    'relative overflow-hidden',
    config.imageSize,
    shapeClass,
    // Shadow variant - always has background and shadow
    variant === 'shadow' && ['shadow-md', !src && 'bg-mf-gray-100'],
    // Ring variant (non-gradient) - outline with optional offset for white padding effect
    variant === 'outline' &&
      !useGradient && [
        'outline',
        config.outlineWidth,
        outlineColorClass,
        withPadding && config.outlineOffset,
      ],
    // Default variant without image: no background (icon/initials float)
  );

  // Gradient ring styles (positioned absolutely outside the avatar)
  const gradientRingStyles = classNames(
    'absolute',
    config.gradientRingInset,
    shapeClass,
    'bg-gradient-to-br from-[#FBE39A] to-[#CAAF63]',
  );

  // White gap layer for gradient ring (when withPadding is true)
  const gradientGapStyles = classNames(
    'absolute',
    config.gradientGapInset,
    shapeClass,
    'bg-white',
  );

  // Render the label section
  const renderLabel = () => {
    if (!hasLabel) return null;

    return (
      <div className="flex flex-col justify-center flex-1 text-left">
        {primaryLabel && (
          <span
            className={classNames('block font-semibold', config.labelPrimary)}
          >
            {primaryLabel}
          </span>
        )}
        {secondaryLabel && (
          <span
            className={classNames(
              'text-gray-500 font-normal',
              config.labelSecondary,
            )}
          >
            {secondaryLabel}
          </span>
        )}
      </div>
    );
  };

  // Render the avatar element (handles both gradient and non-gradient)
  const renderAvatar = () => {
    if (useGradient) {
      return (
        <>
          {/* Gradient ring layer (positioned outside the avatar) */}
          <div className={gradientRingStyles} />
          {/* White gap layer (between gradient and avatar) */}
          {withPadding && <div className={gradientGapStyles} />}
          {/* Avatar content at full size */}
          <div className={avatarStyles}>{renderContent()}</div>
        </>
      );
    }
    return <div className={avatarStyles}>{renderContent()}</div>;
  };

  // If no label, render just the avatar
  if (!hasLabel) {
    return (
      <div className={containerStyles}>
        {renderAvatar()}
        {renderBadge()}
      </div>
    );
  }

  // With label, render avatar + label in a flex row
  return (
    <div
      className={classNames(
        'inline-flex items-center p-3',
        config.labelGap,
        className,
      )}
    >
      <div
        className={classNames(
          'relative inline-flex shrink-0 mr-2',
          config.container,
        )}
      >
        {renderAvatar()}
        {renderBadge()}
      </div>
      {renderLabel()}
    </div>
  );
};

export default Avatar;
